import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	Check,
	CreditCard,
	Loader2,
	WalletCards,
} from "lucide-react";
import { useId, useState } from "react";
import {
	MAIN_CONTENT_ID,
	PublicFooter,
	PublicHeader,
} from "@/components/public-layout";
import { Reveal } from "@/components/reveal";
import { buildPageHead } from "@/lib/seo-meta";

const TOP_UP_PRESETS = [1000, 2500, 5000, 10000];
const currencyFormatter = new Intl.NumberFormat("en-US", {
	currency: "USD",
	maximumFractionDigits: 0,
	style: "currency",
});

export const Route = createFileRoute("/onboard")({
	component: OnboardPage,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: "/onboard",
			title: "Onboard - AgentFramer",
			description:
				"Top up AgentFramer balance before account setup, then connect your AI assistant to create content assets through MCP.",
			ogImage: "pricing",
		}),
});

function formatCurrency(cents: number) {
	return currencyFormatter.format(cents / 100);
}

function OnboardPage() {
	const search = Route.useSearch() as { checkout?: string };
	const emailInputId = useId();
	const [amountCents, setAmountCents] = useState(2500);
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isCheckingOut, setIsCheckingOut] = useState(false);
	const [isClaiming, setIsClaiming] = useState(false);
	const [claimMessage, setClaimMessage] = useState<string | null>(null);

	async function startCheckout() {
		setError(null);
		setIsCheckingOut(true);
		try {
			const response = await fetch("/api/stripe/onboard-checkout", {
				body: JSON.stringify({ amountCents, email }),
				headers: { "Content-Type": "application/json" },
				method: "POST",
			});
			const data = (await response.json()) as {
				checkoutUrl?: string;
				error?: string;
			};
			if (!response.ok || !data.checkoutUrl) {
				throw new Error(data.error || "Could not start checkout");
			}
			window.location.href = data.checkoutUrl;
		} catch (checkoutError) {
			setError(
				checkoutError instanceof Error
					? checkoutError.message
					: "Could not start checkout",
			);
			setIsCheckingOut(false);
		}
	}

	async function claimTopUps() {
		setClaimMessage(null);
		setError(null);
		setIsClaiming(true);
		try {
			const response = await fetch("/api/onboard/claim-topups", {
				method: "POST",
			});
			const data = (await response.json()) as {
				claimedAmountCents?: number;
				claimedCount?: number;
				error?: string;
			};
			if (!response.ok)
				throw new Error(data.error || "Could not claim balance");
			if (data.claimedAmountCents && data.claimedAmountCents > 0) {
				setClaimMessage(
					`Claimed ${formatCurrency(data.claimedAmountCents)} to your workspace.`,
				);
			} else {
				setClaimMessage(
					"No pending top-ups found for this account email yet. If you just paid, wait for Stripe to finish the webhook and try again.",
				);
			}
		} catch (claimError) {
			setError(
				claimError instanceof Error
					? claimError.message
					: "Could not claim balance",
			);
		} finally {
			setIsClaiming(false);
		}
	}

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main id={MAIN_CONTENT_ID} className="flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="flex flex-col gap-px bg-border">
						<div
							aria-hidden="true"
							className="h-14 rounded-md bg-background sm:h-16"
						/>

						<section className="grid gap-px bg-border lg:grid-cols-[1.05fr_0.95fr]">
							<div className="homepage-hover-cell rounded-md bg-background px-6 py-12 sm:px-10 sm:py-16">
								<Reveal>
									<p className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground/70">
										PREPAID ONBOARDING
									</p>
									<h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl">
										Top up first. Create with your agent next.
									</h1>
									<p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
										AgentFramer is fully usage-based. Add prepaid balance before
										or after account creation, then let your assistant generate
										content assets through MCP.
									</p>
									<div className="mt-8 grid gap-3 text-sm text-foreground sm:grid-cols-2">
										{[
											"No trials or subscriptions",
											"Stripe-hosted checkout",
											"Balance links by email after signup",
											"Dashboard top-ups still work later",
										].map((item) => (
											<div key={item} className="flex items-center gap-2">
												<Check className="size-4 text-[#6B3FF2]" />
												<span>{item}</span>
											</div>
										))}
									</div>
								</Reveal>
							</div>

							<div className="homepage-hover-cell rounded-md bg-background p-6 sm:p-8">
								<Reveal delay={80}>
									<div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
										<div className="flex items-center justify-between gap-3">
											<div className="flex size-11 items-center justify-center rounded-lg border border-border bg-background">
												<WalletCards className="size-5 text-[#6B3FF2]" />
											</div>
											<span className="rounded-full border border-[#6B3FF2]/30 bg-[#6B3FF2]/10 px-2 py-0.5 text-[11px] font-medium text-[#6B3FF2]">
												Usage-based
											</span>
										</div>

										<h2 className="mt-5 text-2xl font-semibold">
											Starter balance
										</h2>
										<p className="mt-2 text-sm leading-relaxed text-muted-foreground">
											Choose a prepaid amount and pay through Stripe. After
											signup, claim the balance with the same email.
										</p>

										<label
											className="mt-6 block text-sm font-medium"
											htmlFor={emailInputId}
										>
											Email for balance claim
										</label>
										<input
											id={emailInputId}
											type="email"
											aria-label="Email for balance claim"
											value={email}
											onChange={(event) => setEmail(event.target.value)}
											placeholder="you@company.com"
											className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-[#6B3FF2]"
										/>

										<div className="mt-5 grid grid-cols-2 gap-2">
											{TOP_UP_PRESETS.map((preset) => (
												<button
													key={preset}
													type="button"
													onClick={() => setAmountCents(preset)}
													className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
														amountCents === preset
															? "border-[#6B3FF2] bg-[#6B3FF2]/10 text-[#6B3FF2]"
															: "border-border bg-background text-foreground hover:bg-accent"
													}`}
												>
													{formatCurrency(preset)}
												</button>
											))}
										</div>

										{error ? (
											<p className="mt-4 text-sm text-red-500">{error}</p>
										) : null}
										{claimMessage ? (
											<p className="mt-4 text-sm text-muted-foreground">
												{claimMessage}
											</p>
										) : null}

										<button
											type="button"
											onClick={startCheckout}
											disabled={isCheckingOut}
											className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#6B3FF2] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#5A33D4] disabled:cursor-not-allowed disabled:opacity-70"
										>
											{isCheckingOut ? (
												<Loader2 className="size-4 animate-spin" />
											) : (
												<CreditCard className="size-4" />
											)}
											Top up {formatCurrency(amountCents)}
										</button>

										{search.checkout === "success" ? (
											<div className="mt-4 rounded-md border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300">
												Payment received. Sign in or create your account with
												the same email, then claim the balance.
											</div>
										) : null}

										<div className="mt-4 grid gap-2 sm:grid-cols-2">
											<Link
												to="/signup"
												search={{ redirect: "/onboard?checkout=success" }}
												className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
											>
												Create account
												<ArrowRight className="size-4" />
											</Link>
											<button
												type="button"
												onClick={claimTopUps}
												disabled={isClaiming}
												className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-70"
											>
												{isClaiming ? (
													<Loader2 className="size-4 animate-spin" />
												) : null}
												Claim balance
											</button>
										</div>
									</div>
								</Reveal>
							</div>
						</section>

						<section className="grid gap-px bg-border md:grid-cols-3">
							{[
								[
									"1",
									"Top up",
									"Pay first so every generation is backed by prepaid balance.",
								],
								[
									"2",
									"Create account",
									"Use the same email to link your Stripe payment to a workspace.",
								],
								[
									"3",
									"Connect MCP",
									"Generate images, video, and audio from your assistant.",
								],
							].map(([step, title, body]) => (
								<div
									key={step}
									className="homepage-hover-cell rounded-md bg-background p-8"
								>
									<p className="font-mono text-xs text-muted-foreground">
										STEP {step}
									</p>
									<h2 className="mt-3 text-xl font-semibold">{title}</h2>
									<p className="mt-2 text-sm leading-relaxed text-muted-foreground">
										{body}
									</p>
								</div>
							))}
						</section>
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
